import { Controller } from "@hotwired/stimulus";
import WebAuthnCodec from "../services/WebAuthnCodec";

// Passkey registration ceremony against the native WebAuthn browser API.
// Server-generated creation options are fetched first; the authenticator
// response is posted back to Core. The private key never leaves the
// authenticator and no bearer material enters browser storage.
export default class extends Controller {
  static values = {
    registerOptionsUrl: String,
    registerUrl: String,
    label: String,
  };

  static targets = ["button", "status"];

  async register() {
    this.setBusy(true);
    this.showError("");
    try {
      const optionsResponse = await fetch(this.registerOptionsUrlValue, {
        headers: { Accept: "application/json" },
      });
      if (optionsResponse.status === 403) {
        this.showError(this.recentAuthText());
        this.setBusy(false);
        return;
      }
      if (!optionsResponse.ok) throw new Error("options");
      const options = await optionsResponse.json();

      const publicKey = WebAuthnCodec.decodeCreationOptions(options);
      const attestation = await navigator.credentials.create({ publicKey });
      const credential = WebAuthnCodec.encodeAttestation(attestation);

      const registerResponse = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify({ credential, label: this.labelValue }),
      });
      const result = await registerResponse.json();
      if (!registerResponse.ok) throw new Error(result.error || "register");
      if (result.recovery_html) {
        const parsed = new DOMParser().parseFromString(
          result.recovery_html,
          "text/html"
        );
        document.title = parsed.title;
        document.open();
        document.write(result.recovery_html);
        document.close();
        window.scrollTo({ top: 0, behavior: "instant" });
      } else {
        window.location.reload();
      }
    } catch (error) {
      this.showError(
        error.name === "NotAllowedError" || error.message === "cancelled"
          ? this.cancelledText()
          : this.failureText()
      );
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = busy;
    if (this.hasButtonTarget) this.buttonTarget.setAttribute("aria-busy", busy);
  }

  showError(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = message;
    this.statusTarget.hidden = !message;
    if (message) this.statusTarget.focus();
  }

  failureText() {
    return (
      this.element.dataset.passkeyRegistrationFailureText ||
      "Passkey setup failed. Try again or choose another method."
    );
  }

  cancelledText() {
    return (
      this.element.dataset.passkeyRegistrationCancelledText ||
      "Passkey setup was cancelled. Choose another method."
    );
  }

  recentAuthText() {
    return (
      this.element.dataset.passkeyRegistrationRecentAuthText ||
      "Please verify your identity again and try once more."
    );
  }
}
